import * as audioTools from "./audioTools";

export default class AudioKey {
	// Supplied
	pitch = 0;
	mode = 0;

	// Derived
	accidentalType = "";
	accidentalCount = 0;
	pitches = [];
	pitchNames = [];

	constructor(pitch, mode, loadDerivedData = true) {
		this.pitch = pitch;
		this.mode = mode;

		if (loadDerivedData) {
			this._loadDerivedData();
		}
	}

	name() {
		return this.pitchNames[0] + " " + audioTools.modes[this.mode];
	}

	richName() {
		return audioTools.getRichPitchName(this.pitchNames[0]) + " " +
			audioTools.modes[this.mode];
	}

	urlName() {
		return audioTools.getKeyUrlName(this.name());
	}

	number() {
		return audioTools.numericKey(this.pitch, this.mode);
	}

	segmentedNumber() {
		return audioTools.segmentedNumber(this.pitch, this.mode);
	}

	richPitchNames() {
		return this.pitchNames.map(audioTools.getRichPitchName);
	}

	getPitchName(pitch) {
		let index = this.pitches.indexOf(pitch);

		if (index >= 0) {
			return this.pitchNames[index];
		}

		return "";
	}

	getRichPitchName(pitch) {
		return audioTools.getRichPitchName(this.getPitchName(pitch));
	}

	// Loading derived data...

	_loadDerivedData() {
		let data = AudioKey.loadDerivedDataFor(this.pitch, this.mode);

		let {
			accidentalCount,
			accidentalType,
			pitches,
			pitchNames
		} = data;

		this.accidentalCount = accidentalCount;
		this.accidentalType = accidentalType;
		this.pitches = pitches;
		this.pitchNames = pitchNames;
	}

	static loadDerivedDataFor(pitch, mode) {
		let majorPitch = audioTools.getRelativeMajorPitch(pitch, mode);
		let pitches = audioTools.getScalePitches(pitch, mode);
		let pitchNames = pitches.map((p) => audioTools.pitchNames[p]);

		let currentLetter = "";
		let accidentalCount = 0;
		let accidentalType = null;

		pitchNames.forEach((names, i) => {
			let name = null;

			if (i === 0) {
				// First one
				// If there's only once choice, pick that...

				if (names.length === 1) {
					name = names[0];
				}

				// If it's not a major, pick the one the relative major key goes with

				else if (majorPitch !== pitch) {
					if (majorPitch === 6 && pitch === 3) {
						// Exception for E-flat minor

						// Reason being: In this case, it is an equal amount of
						// accidentals if this were achieved using either flats or sharps.

						// And in this case, if we use E-flat rather than D-sharp, we
						// avoid introducing a new letter-accidental combination on the
						// major+minor keys list (we already use E-flat for E-flat major,
						// and do not use D-sharp anywhere else on the list)

						// This achives an, admittedly slighty, simpler list.

						name = names[0];
					}

					else {
						let majorInfo = this.loadDerivedDataFor(
							majorPitch, audioTools.MODE_MAJOR
						);

						name = names.reduce((pick, n) => {
							let { accidentalType: at } = majorInfo;

							if (
								// Has identical accidental
								(at && n.indexOf(at) >= 0) ||
								// Natural
								n.length <= 1
							) {
								return n;
							}

							return pick;
						});
					}
				}

				// If it is, just pick the first name out of the choices

				else {
					name = names[0];
				}
			}

			else {
				let letter = this.getNextLetter(currentLetter);
				name = names.find((p) => p[0] === letter);

				if (!name) {
					// This is all but proven not to happen
					throw Error("Unable to find letter name for pitch", names);
				}
			}

			let letter = name[0];
			pitchNames[i] = name;
			currentLetter = letter;

			if (name !== letter) {
				// Register accidentals
				let accidental = name.split("-")[1];
				accidentalType = accidental;
				accidentalCount++;
			}
		});

		return {
			accidentalCount,
			accidentalType,
			pitches,
			pitchNames
		};
	}

	static getNextLetter(letter) {
		let { letters } = audioTools;
		let index = letters.indexOf(letter);
		let nextIndex = index + 1;

		if (
			typeof index !== "number" ||
			index < 0 ||
			index >= letters.length - 1
		) {
			nextIndex = 0;
		}

		return letters[nextIndex];
	}
}
